import { LocalizedDate } from '@/components/Atoms/LocalizedDate/LocalizedDate';
import { P } from '@/components/Atoms/P/P';
import { CenteredPageContent } from '@/components/Layouts/CenteredPageContent';
import type { IBreadcrumbItem } from '@/components/Molecules/Breadcrumb/Breadcrumb';
import { Breadcrumb } from '@/components/Molecules/Breadcrumb/Breadcrumb';
import { Stack } from '@/components/Molecules/Stack/Stack';
import useTranslation from '@/hooks/useTranslation';
import classNames from '@/utils/classNames';
import Trans from 'next-translate/Trans';
import type { ReactNode } from 'react';
import type { FCC, FCl } from 'react-fc';
import styles from './PageHeader.module.css';

/**
 * The main header of most pages.
 */
export const PageHeader: FCC<IProps> = ({
    title,
    breadCrumbs,
    callToAction,
    children,
    bylineName,
    date,
    asUnpublished,
    status,
    mainAction,
    headingElement = null,
    sticky = false,
    className,
    lastUpdated,
}) => {
    const HeadingTag = headingElement || 'h1';

    return (
        <div
            className={classNames(styles.wrapper, {
                [styles.sticky]: sticky,
            })}
        >
            <header className={classNames(styles.header, className)}>
                <CenteredPageContent className={styles.headerContent}>
                    <Breadcrumb items={breadCrumbs} />
                    <div className={classNames(styles.heading)}>
                        <Stack>
                            {status}
                            <HeadingTag className="cds-t-h1">{title}</HeadingTag>
                        </Stack>
                        {callToAction && <div>{callToAction}</div>}
                    </div>
                    {mainAction && <div className={styles.mainAction}>{mainAction}</div>}
                    {children && (
                        <P className={styles.intro} as="div">
                            {children}
                        </P>
                    )}
                    <Byline
                        asUnpublished={asUnpublished}
                        date={date}
                        bylineName={bylineName}
                        lastUpdated={lastUpdated}
                    />
                </CenteredPageContent>
            </header>
        </div>
    );
};

export const BasicPageHeaderWrapper: FCC<IPageHeaderWrapperProps> = ({ breadCrumbs, children, className }) => {
    return (
        <header className={classNames(styles.header, className)}>
            <CenteredPageContent>
                <Breadcrumb items={breadCrumbs} />
                <div className={styles.heading}>{children}</div>
            </CenteredPageContent>
        </header>
    );
};

interface IPageHeaderWrapperProps {
    /**
     * Use an empty array if this page is top level
     */
    breadCrumbs: IBreadcrumbItem[];
}

interface IProps {
    /**
     * Use an empty array if this page is top level
     */
    breadCrumbs: IBreadcrumbItem[];
    /**
     * The main title
     */
    title: string;
    /**
     * ISO String. Used as a publication date to display
     */
    date?: string;
    /**
     * An optional byline name if a full author cannot be used
     */
    bylineName?: string;
    /**
     * For the rare occasion this should not be a \<h1>, specify which element the title should be
     * @default h1
     */
    headingElement?: any;
    /**
     * Should the header become sticky as the page scrolls down?
     * @default false
     */
    sticky?: boolean;
    /**
     * An optional Call To Action Node to be placed alongside the title
     */
    callToAction?: ReactNode;
    /**
     * Show the date/byline as UNPUBLISHED instead of the usual date
     */
    asUnpublished?: boolean;
    /**
     * Show "Last updated on ..." instead of "published by ..."
     */
    lastUpdated?: string;
    /**
     * Show a status pill for the page
     */
    status?: ReactNode;
    mainAction?: ReactNode;
}

export const Byline: FCl<Partial<IProps>> = ({ bylineName, asUnpublished, date, lastUpdated, className }) => {
    const { t } = useTranslation('common');

    if (!date && !bylineName && !lastUpdated) {
        return null;
    }

    let byline;
    if (date && bylineName) {
        byline = (
            <Trans
                i18nKey={'common:PageHeader.fullByline'}
                components={{ date: <LocalizedDate date={date} /> }}
                values={{ name: bylineName }}
            />
        );
    } else if (lastUpdated) {
        byline = (
            <Trans
                i18nKey={'common:PageHeader.dateUpdatedByline'}
                components={{ date: <LocalizedDate date={lastUpdated} /> }}
            />
        );
    } else if (date) {
        byline = (
            <Trans i18nKey={'common:PageHeader.dateByline'} components={{ date: <LocalizedDate date={date} /> }} />
        );
    } else if (bylineName) {
        byline = t('common:PageHeader.nameByline', { name: <span>{bylineName}</span> });
    }

    return (
        <p
            className={classNames(className, styles.byline, {
                [styles.future]: date && new Date(date) > new Date(),
                [styles.unpublished]: asUnpublished,
            })}
        >
            {byline}
        </p>
    );
};
